import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { resizeImage } from "../../../services/screenshot.service"
import { useVentilatedWall } from "../../../store/ventilated-wall/hooks"
import { useModal } from "../../../store/modal/hooks"
import { renderer } from "../../viewer/Renderer"
import { useApplication } from "../../../store/application/hooks"
import { isMobile } from "../../../services/helpers.service"
import { useAccountState } from "../../providers/AccountStateProvider"
import { useQueryParams } from "../../../store/query/ventilated-wall.hooks"
import { VentilatedWallData } from "../../../store/ventilated-wall/store"
import { Models } from "../../viewer/renderer.api"
import { ApplicationTypes } from "../../../store/application/store"

type VisibleState = {
  wallType: boolean
  facadeCladding: boolean
  wallInsulation: boolean
  connectionProfile: boolean
  backfaceConstruction: boolean
  details: boolean
}

export function useOverview() {
  const account = useAccountState()
  const { t, i18n } = useTranslation() as any
  const [, country] = i18n.language.split("_")
  const [linkCopied, setLinkCopied] = useState<boolean>(false)
  const showExtendedThermalCalculation = country === "nl" && !isMobile()
  const {
    configurationState: { wallType, facadeCladding, wallInsulation },
    thermalPerformance,
    setOverviewScreenshot,
    setHumanReadableName,
  } = useVentilatedWall()

  const wallTypeType = wallType.type!
  const facadeCladdingType = facadeCladding.type!
  const wallInsulationType = wallInsulation.type!

  const {
    displayVentilatedWallDownloadModal,
    displayVentilatedWallThermalCalculationModal,
    displayVentilatedWallExtendedThermalCalculationModal,
    displayRequestAdviceModal,
    displaySaveConfigurationModal,
    displaySaveConfigurationSuccessModal,
  } = useModal()
  const {
    currentModel,
    applicationType,
    directLink,
    setDirectLink,
  } = useApplication()
  const { setQueryParams } = useQueryParams()

  const initialVisibleState = useMemo(
    () => ({
      wallType: true,
      facadeCladding: true,
      wallInsulation: true,
      connectionProfile: true,
      backfaceConstruction: true,
      details: true,
    }),
    []
  )
  const [visibleState, setVisibleState] = useState<VisibleState>(
    initialVisibleState
  )
  const humanReadableName = `${wallType.type} / ${facadeCladding.type} / ${wallInsulation.type}`

  useEffect(() => {
    setHumanReadableName(humanReadableName)
  }, [humanReadableName, setHumanReadableName])

  useEffect(() => {
    if (!directLink) {
      const data: VentilatedWallData = {
        wallType,
        facadeCladding,
        wallInsulation,
        thermalPerformance,
      }
      setQueryParams(data, i18n.language)
    }

    // Handle direct link
    if (renderer && currentModel === Models.DOLLHOUSE && directLink) {
      setDirectLink(false)
      setTimeout(() => {
        let model = Models.VENTILATED_WALL
        renderer.displayModel(model).then(() => {
          renderer.displayVentilatedWallWallType(wallTypeType)
          renderer.toggleLayer("BINNENSPOUWBLAD", true)
          renderer.displayVentilatedWallFacadeType(facadeCladdingType)
          renderer.toggleLayer("GEVELBEKLEDING", true)
          renderer.displayVentilatedWallInsulation(wallInsulationType)
          renderer.toggleLayer("ISOLATIE", true)
        })
      }, 0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thermalPerformance, wallType, facadeCladding, wallInsulation, setQueryParams, directLink])

  useEffect(
    () => {
      if (!isMobile()) renderer.displayMarkers()
      const dataURL = renderer.createScreenshot()
      resizeImage(dataURL, 520, 420).then((resizedDataURL) => {
        setOverviewScreenshot(resizedDataURL)
      })

      return () => {
        renderer.hideMarkers()
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setOverviewScreenshot]
  )

  function toggleVisibleState(key: keyof VisibleState) {
    setVisibleState({ ...visibleState, [key]: !visibleState[key] })
  }

  useEffect(() => {
    setVisibleState(initialVisibleState)
    if (!isMobile()) renderer.displayMarkers()
    renderer.toggleAllLayers(applicationType, true)
  }, [currentModel, setVisibleState, initialVisibleState, applicationType])

  function handleToggleClick(toggle = "wallType", layer = "BINNENSPOUWBLAD") {
    toggleVisibleState(toggle as keyof VisibleState)
    renderer.toggleLayers(layer)
  }

  function handleDetailsClick() {
    toggleVisibleState("details")
    renderer.toggleDetails()
  }

  function handleAdjustSpecificationsClick() {
    if (showExtendedThermalCalculation) {
      displayVentilatedWallExtendedThermalCalculationModal()
    } else {
      displayVentilatedWallThermalCalculationModal()
    }
  }

  function handleDownloadFilesClick() {
    displayVentilatedWallDownloadModal()
  }

  function handleRequestAdviceClick() {
    displayRequestAdviceModal()
  }

  const copyShareLink = () => {
    const link = window.location.href
    if (navigator && navigator.clipboard) {
      navigator.clipboard.writeText(link).then(() => setLinkCopied(true))
    } else {
      window.prompt(t("flatRoof.overview.shareConfiguration"), link)
      setLinkCopied(true)
    }
  }

  function handleSaveConfigurationClick() {
    if (!account.isAuthenticated) return

    displaySaveConfigurationModal(async (name: string) => {
      const ventilatedWallData: VentilatedWallData = {
        wallType: wallType,
        facadeCladding: facadeCladding,
        wallInsulation: wallInsulation,
        thermalPerformance: thermalPerformance,
      }
      const ventilatedWallConfiguration = {
        application_type: ApplicationTypes.OUTER_WALL,
        application_data: ventilatedWallData,
        display_name: name ?? `${t("home.outerWall")}`,
        language: i18n.language,
      }
      account
        .saveConfiguration(ventilatedWallConfiguration)
        .then((response) => {
          displaySaveConfigurationSuccessModal(
            response?.saved_configuration?.record_id
          )
        })
        .catch(() => {
          console.error("Failed to save configuration")
        })
    })
  }

  return {
    t,
    currentModel,
    thermalPerformance,
    visibleState,
    linkCopied,

    wallTypeType,
    facadeCladdingType,
    wallInsulationType,

    handleToggleClick,
    handleDetailsClick,

    handleAdjustSpecificationsClick,
    handleDownloadFilesClick,
    handleRequestAdviceClick,

    copyShareLink,
    handleSaveConfigurationClick,
  }
}
